/**
 * Client-side list controls shared by the Barratt list pages: supplier search,
 * click-to-sort on any column, and paging with 100 / 250 / 500 rows shown at
 * the top and bottom of a table. Everything runs over rows already in memory.
 *
 * @example
 * const list = useListControls(rows, {
 *   searchText: (r) => r.supplier,
 *   sorters: { supplier: (r) => r.supplier.toLowerCase(), score: (r) => r.score ?? 0 },
 *   defaultSort: { key: 'score', dir: 'asc' },
 * });
 * <ListPager list={list} position="top" />
 * <SortableTh field="score" sortField={list.sortKey} sortDirection={list.sortDir}
 *             onSort={list.sortBy}>Score</SortableTh>
 * {list.pageRows.map(...)}
 *
 * Sort the header with `SortableTh` (`components/common/SortableTh.tsx`), which
 * this hook drives directly. The older `<TableHead onClick={…}>{list.sortIcon()}`
 * shape is not keyboard-reachable and announces no `aria-sort`; `sortIcon` stays
 * only for the callers still on it.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight, Search } from 'lucide-react';

export const LIST_PAGE_SIZES = [100, 250, 500] as const;

/**
 * The rule the search box applies: trimmed, case-insensitive substring.
 *
 * Exported so a caller that has to count rows outside the list — tab counts,
 * for instance — can count exactly what the list will show, rather than a
 * second implementation that drifts from this one.
 *
 * @param search - the raw search box value
 * @param text - the row's searchable text
 * @returns true when the row belongs in the results
 */
export function matchesSearch(search: string, text: string): boolean {
  const term = search.trim().toLowerCase();
  return term.length === 0 || text.toLowerCase().includes(term);
}
export type SortDir = 'asc' | 'desc';
export type SortValue = number | string | null;

export interface ListControlsOptions<T> {
  /** Text the search box matches against (case-insensitive substring). */
  searchText: (row: T) => string;
  /** Sort keys → value extractors. `null` values always sort last. */
  sorters: Record<string, (row: T) => SortValue>;
  defaultSort: { key: string; dir: SortDir };
  /** Stable name used to tie-break equal values. */
  tieBreak?: (row: T) => string;
  /** Anything that should reset the page to 1 when it changes. */
  resetKeys?: unknown[];
}

export interface ListControls<T> {
  search: string;
  setSearch: (value: string) => void;
  sortKey: string;
  sortDir: SortDir;
  sortBy: (key: string) => void;
  sortIcon: (key: string) => ReactNode;
  pageSize: number;
  setPageSize: (size: number) => void;
  page: number;
  setPage: (page: number) => void;
  pageCount: number;
  /** Rows after search and sort — every page. Use this for exports. */
  orderedRows: T[];
  /** Rows on the current page. */
  pageRows: T[];
  /** 1-based rank of the first row on the current page. */
  firstRank: number;
}

/**
 * Search, sort and page a list of rows already in memory.
 *
 * @param rows - the full list
 * @param options - how to search, sort and tie-break
 * @returns state, setters and the derived `orderedRows` / `pageRows`
 */
export function useListControls<T>(rows: T[], options: ListControlsOptions<T>): ListControls<T> {
  const { searchText, sorters, defaultSort, tieBreak, resetKeys = [] } = options;
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState(defaultSort.key);
  const [sortDir, setSortDir] = useState<SortDir>(defaultSort.dir);
  const [pageSize, setPageSize] = useState<number>(LIST_PAGE_SIZES[0]);
  const [page, setPage] = useState(1);

  const orderedRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = term ? rows.filter((r) => matchesSearch(search, searchText(r))) : rows;
    const value = sorters[sortKey] ?? (() => null);
    const sign = sortDir === 'asc' ? 1 : -1;
    const tie = tieBreak ?? (() => '');
    return [...filtered].sort((a, b) => {
      const va = value(a); const vb = value(b);
      if (va === null && vb === null) return tie(a).localeCompare(tie(b));
      if (va === null) return 1;
      if (vb === null) return -1;
      if (va < vb) return -1 * sign;
      if (va > vb) return 1 * sign;
      return tie(a).localeCompare(tie(b));
    });
  }, [rows, search, sortKey, sortDir, searchText, sorters, tieBreak]);

  const pageCount = Math.max(1, Math.ceil(orderedRows.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const pageRows = orderedRows.slice((safePage - 1) * pageSize, safePage * pageSize);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); }, [search, sortKey, sortDir, pageSize, ...resetKeys]);

  const sortBy = (key: string) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };
  const sortIcon = (key: string): ReactNode =>
    sortKey !== key ? <ArrowUpDown className="h-3 w-3 ml-1 inline text-muted-foreground" />
      : sortDir === 'asc' ? <ArrowUp className="h-3 w-3 ml-1 inline" /> : <ArrowDown className="h-3 w-3 ml-1 inline" />;

  return {
    search, setSearch, sortKey, sortDir, sortBy, sortIcon,
    pageSize, setPageSize, page: safePage, setPage, pageCount,
    orderedRows, pageRows, firstRank: (safePage - 1) * pageSize + 1,
  };
}

interface ListPagerProps<T> {
  list: ListControls<T>;
  position: 'top' | 'bottom';
  /** Noun for the count line, e.g. "suppliers". */
  noun?: string;
  testIdPrefix?: string;
}

/** "Showing a–b of n · Show 100/250/500 · ‹ Page x of y ›" for one end of a table. */
export function ListPager<T>({ list, position, noun = 'rows', testIdPrefix = 'list' }: ListPagerProps<T>) {
  const { orderedRows, page, pageCount, pageSize, setPageSize, setPage } = list;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3" data-testid={`${testIdPrefix}-pager-${position}`}>
      <span className="text-sm text-muted-foreground">
        {orderedRows.length === 0
          ? `No ${noun} match`
          : `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, orderedRows.length)} of ${orderedRows.length}`}
      </span>
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Show</span>
        <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
          <SelectTrigger className="w-20" aria-label="Rows per page"><SelectValue /></SelectTrigger>
          <SelectContent>
            {LIST_PAGE_SIZES.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page <= 1} aria-label="Previous page">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm tabular-nums">Page {page} of {pageCount}</span>
        <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page >= pageCount} aria-label="Next page">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

interface ListSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  testId?: string;
}

/** The search box that pairs with `useListControls`. */
export function ListSearch({ value, onChange, placeholder, testId = 'list-search' }: ListSearchProps) {
  return (
    <div className="relative w-72 max-w-full">
      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
             className="pl-8" aria-label={placeholder} data-testid={testId} />
    </div>
  );
}
